import { useState, useRef, useCallback, useMemo } from 'react';
import { AgentVacation } from '@/services/vacationService';
import { MoonIcon } from '@heroicons/react/24/outline';

interface GanttChartProps {
  vacations: AgentVacation[];
  selectedDate: string;
  onUpdateVacation: (id: number, heureDebut: string, heureFin: string) => void;
  viewMode: 'day' | 'week';
  weekDates?: string[];
}

const HOUR_WIDTH = 60; // pixels par heure
const ROW_HEIGHT = 50;
const HEADER_HEIGHT = 40;
const AGENT_COL_WIDTH = 200;

const STATUT_COLORS: Record<string, string> = {
  planifiee: 'bg-blue-500 hover:bg-blue-600',
  en_cours: 'bg-orange-500 hover:bg-orange-600',
  terminee: 'bg-green-500 hover:bg-green-600',
  annulee: 'bg-gray-400',
};

const STATUT_LABELS: Record<string, string> = {
  planifiee: 'Planifiée',
  en_cours: 'En cours',
  terminee: 'Terminée',
  annulee: 'Annulée',
};

export function GanttChart({
  vacations,
  selectedDate,
  onUpdateVacation,
  viewMode,
  weekDates = [],
}: GanttChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<{
    vacationId: number;
    type: 'move' | 'resize-left' | 'resize-right';
    startX: number;
    originalStart: number;
    originalEnd: number;
  } | null>(null);
  const [previewState, setPreviewState] = useState<{
    vacationId: number;
    startMinutes: number;
    endMinutes: number;
  } | null>(null);

  // Heures affichées (0h à 24h)
  const hours = useMemo(() => Array.from({ length: 25 }, (_, i) => i), []);

  // Grouper les vacations par agent
  const vacationsByAgent = useMemo(() => {
    const grouped = new Map<string, { agent: AgentVacation['agent']; vacations: AgentVacation[] }>();

    vacations.forEach((v) => {
      const key = String(v.agent.id);
      if (!grouped.has(key)) {
        grouped.set(key, { agent: v.agent, vacations: [] });
      }
      grouped.get(key)!.vacations.push(v);
    });

    return Array.from(grouped.values()).sort((a, b) =>
      a.agent.nomComplet.localeCompare(b.agent.nomComplet)
    );
  }, [vacations]);

  // Convertir heure en minutes depuis minuit
  // Gère plusieurs formats: "HH:MM", "HH:MM:SS", ISO datetime
  const timeToMinutes = (time: string): number => {
    if (!time) return 0;

    // Si c'est un format ISO datetime (ex: "1970-01-01T06:00:00+00:00")
    if (time.includes('T')) {
      const timePart = time.split('T')[1];
      const [h, m] = timePart.split(':').map(Number);
      return h * 60 + m;
    }

    // Format "HH:MM" ou "HH:MM:SS"
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  // Convertir minutes en heure formatée
  const minutesToTime = (minutes: number): string => {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Formater l'heure pour l'affichage (extraire HH:MM de différents formats)
  const formatTimeDisplay = (time: string): string => {
    if (!time) return '??:??';

    // Si c'est un format ISO datetime
    if (time.includes('T')) {
      const timePart = time.split('T')[1];
      return timePart.substring(0, 5);
    }

    // Format "HH:MM" ou "HH:MM:SS"
    return time.substring(0, 5);
  };

  // Convertir minutes en position X
  const minutesToX = (minutes: number): number => {
    return (minutes / 60) * HOUR_WIDTH;
  };

  // Convertir position X en minutes
  const xToMinutes = (x: number): number => {
    const minutes = (x / HOUR_WIDTH) * 60;
    // Arrondir aux 15 minutes les plus proches
    return Math.round(minutes / 15) * 15;
  };

  // Calculer la position et largeur d'une barre de vacation
  const getBarStyle = (vacation: AgentVacation) => {
    let startMinutes = timeToMinutes(vacation.heureDebut);
    let endMinutes = timeToMinutes(vacation.heureFin);

    // Vacation de nuit : si fin < début, ajouter 24h à la fin
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }

    // Utiliser les valeurs de preview si c'est la vacation en cours de modification
    if (previewState && previewState.vacationId === vacation.id) {
      startMinutes = previewState.startMinutes;
      endMinutes = previewState.endMinutes;
    }

    const left = minutesToX(startMinutes);
    const width = minutesToX(endMinutes) - minutesToX(startMinutes);

    return { left, width, startMinutes, endMinutes };
  };

  // Gérer le début du drag
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, vacation: AgentVacation, type: 'move' | 'resize-left' | 'resize-right') => {
      if (vacation.statut !== 'planifiee') return;

      e.preventDefault();
      e.stopPropagation();

      const { startMinutes, endMinutes } = getBarStyle(vacation);

      setDragState({
        vacationId: vacation.id,
        type,
        startX: e.clientX,
        originalStart: startMinutes,
        originalEnd: endMinutes,
      });

      setPreviewState({
        vacationId: vacation.id,
        startMinutes,
        endMinutes,
      });
    },
    []
  );

  // Gérer le mouvement du drag
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragState || !previewState) return;

      const deltaX = e.clientX - dragState.startX;
      const deltaMinutes = xToMinutes(deltaX) - xToMinutes(0);

      let newStart = dragState.originalStart;
      let newEnd = dragState.originalEnd;

      if (dragState.type === 'move') {
        newStart = dragState.originalStart + deltaMinutes;
        newEnd = dragState.originalEnd + deltaMinutes;
      } else if (dragState.type === 'resize-left') {
        newStart = dragState.originalStart + deltaMinutes;
      } else if (dragState.type === 'resize-right') {
        newEnd = dragState.originalEnd + deltaMinutes;
      }

      // Contraintes
      newStart = Math.max(0, Math.min(newStart, 24 * 60 - 15));
      newEnd = Math.max(newStart + 15, Math.min(newEnd, 48 * 60)); // Permettre jusqu'à J+1

      // Minimum 15 minutes de durée
      if (newEnd - newStart < 15) {
        if (dragState.type === 'resize-left') {
          newStart = newEnd - 15;
        } else {
          newEnd = newStart + 15;
        }
      }

      setPreviewState({
        vacationId: dragState.vacationId,
        startMinutes: newStart,
        endMinutes: newEnd,
      });
    },
    [dragState, previewState]
  );

  // Gérer la fin du drag
  const handleMouseUp = useCallback(() => {
    if (!dragState || !previewState) {
      setDragState(null);
      setPreviewState(null);
      return;
    }

    // Vérifier si quelque chose a changé
    const hasChanged =
      previewState.startMinutes !== dragState.originalStart ||
      previewState.endMinutes !== dragState.originalEnd;

    if (hasChanged) {
      const heureDebut = minutesToTime(previewState.startMinutes);
      let heureFin = minutesToTime(previewState.endMinutes % (24 * 60));

      onUpdateVacation(dragState.vacationId, heureDebut, heureFin);
    }

    setDragState(null);
    setPreviewState(null);
  }, [dragState, previewState, onUpdateVacation]);

  // Gérer la sortie de la zone
  const handleMouseLeave = useCallback(() => {
    if (dragState) {
      handleMouseUp();
    }
  }, [dragState, handleMouseUp]);

  const totalWidth = AGENT_COL_WIDTH + hours.length * HOUR_WIDTH;
  const totalHeight = HEADER_HEIGHT + vacationsByAgent.length * ROW_HEIGHT;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div
        ref={containerRef}
        className="overflow-x-auto overflow-y-auto"
        style={{ maxHeight: '600px' }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div style={{ width: totalWidth, minHeight: totalHeight }} className="relative">
          {/* Header avec les heures */}
          <div
            className="sticky top-0 bg-gray-50 border-b z-20 flex"
            style={{ height: HEADER_HEIGHT }}
          >
            {/* Colonne agent */}
            <div
              className="sticky left-0 bg-gray-50 border-r flex items-center px-4 font-medium text-gray-700 z-30"
              style={{ width: AGENT_COL_WIDTH, minWidth: AGENT_COL_WIDTH }}
            >
              Agent
            </div>
            {/* Heures */}
            <div className="flex">
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="border-r border-gray-200 flex items-center justify-center text-xs text-gray-500"
                  style={{ width: HOUR_WIDTH }}
                >
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>
          </div>

          {/* Lignes des agents */}
          {vacationsByAgent.map(({ agent, vacations: agentVacations }, rowIndex) => (
            <div
              key={agent.id}
              className="flex border-b hover:bg-gray-50"
              style={{ height: ROW_HEIGHT }}
            >
              {/* Colonne nom agent */}
              <div
                className="sticky left-0 bg-white border-r flex items-center px-4 z-10"
                style={{ width: AGENT_COL_WIDTH, minWidth: AGENT_COL_WIDTH }}
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-primary-600 text-xs font-medium">
                    {agent.prenom?.[0]}{agent.nom?.[0]}
                  </span>
                </div>
                <div className="truncate">
                  <div className="font-medium text-gray-900 text-sm truncate">{agent.nomComplet}</div>
                </div>
              </div>

              {/* Zone du planning avec grille horaire */}
              <div className="relative flex-1" style={{ width: hours.length * HOUR_WIDTH }}>
                {/* Grille verticale */}
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className="absolute top-0 bottom-0 border-r border-gray-100"
                    style={{ left: hour * HOUR_WIDTH }}
                  />
                ))}

                {/* Barres de vacation */}
                {agentVacations.map((vacation) => {
                  const { left, width } = getBarStyle(vacation);
                  const isDragging = dragState?.vacationId === vacation.id;
                  const isEditable = vacation.statut === 'planifiee';

                  return (
                    <div
                      key={vacation.id}
                      className={`absolute top-2 bottom-2 rounded-md shadow-sm transition-shadow ${
                        STATUT_COLORS[vacation.statut]
                      } ${isDragging ? 'shadow-lg ring-2 ring-primary-300 z-10' : ''} ${
                        isEditable ? 'cursor-move' : 'cursor-default'
                      }`}
                      style={{
                        left: left,
                        width: Math.max(width, 30),
                        opacity: isDragging ? 0.9 : 1,
                      }}
                      onMouseDown={(e) => isEditable && handleMouseDown(e, vacation, 'move')}
                      title={`${vacation.agent.nomComplet}\n${vacation.heureDebut} - ${vacation.heureFin}\n${STATUT_LABELS[vacation.statut]}`}
                    >
                      {/* Handle gauche pour resize */}
                      {isEditable && (
                        <div
                          className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-l-md"
                          onMouseDown={(e) => handleMouseDown(e, vacation, 'resize-left')}
                        />
                      )}

                      {/* Contenu de la barre */}
                      <div className="h-full flex items-center justify-center px-2 overflow-hidden">
                        <span className="text-white text-xs font-medium truncate flex items-center gap-1">
                          {vacation.isVacationNuit && <MoonIcon className="w-3 h-3" />}
                          {width > 80 && (
                            <>
                              {formatTimeDisplay(vacation.heureDebut)} - {formatTimeDisplay(vacation.heureFin)}
                            </>
                          )}
                        </span>
                      </div>

                      {/* Handle droite pour resize */}
                      {isEditable && (
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-r-md"
                          onMouseDown={(e) => handleMouseDown(e, vacation, 'resize-right')}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Message si aucune vacation */}
          {vacationsByAgent.length === 0 && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              Aucune vacation planifiée pour cette période
            </div>
          )}
        </div>
      </div>

      {/* Légende */}
      <div className="px-4 py-3 bg-gray-50 border-t flex items-center gap-6 text-sm">
        <span className="text-gray-500 font-medium">Légende :</span>
        {Object.entries(STATUT_COLORS).map(([statut, color]) => (
          <div key={statut} className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded ${color.split(' ')[0]}`} />
            <span className="text-gray-600">{STATUT_LABELS[statut]}</span>
          </div>
        ))}
        <div className="ml-auto text-gray-500 text-xs">
          <MoonIcon className="w-4 h-4 inline mr-1" />
          = Vacation de nuit (J+1)
        </div>
      </div>

      {/* Instructions */}
      <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 text-xs text-blue-700">
        <strong>Astuce :</strong> Glissez une barre bleue (planifiée) pour modifier l'horaire.
        Utilisez les bords gauche/droit pour ajuster l'heure de début/fin.
      </div>
    </div>
  );
}

export default GanttChart;
